// Where to send someone who lands on the login card while already signed in.
//
// `return` is attacker-supplied (it comes off the URL), and it is also the
// source of the loop this exists for: the post-auth redirects follow it
// blindly, so a `return` pointing back at /login drops a freshly authenticated
// user on the login card again. Neither case may be followed.

import { isAuthRoute } from '@shared/utils/authRoutes';

// The bare url, keeping the language prefix the current page carries.
// `BrowserRouter` is mounted on `basename={'/' + currentLanguage}` and App
// redirects in render when the url has no language, so a plain '/' costs the
// user an extra full page load on the way in.
const bareUrl = (pathname: string): string => {
  const [, first = ''] = pathname.split('/');
  return /^[a-z]{2}$/i.test(first) ? `/${first}` : '/';
};

export const getAfterLoginDestination = (
  href: string = location.href,
): string => {
  const { searchParams, origin, pathname } = new URL(href);
  const returnTo = searchParams.get('return');
  if (returnTo == null) {
    return bareUrl(pathname);
  }
  try {
    const url = new URL(returnTo, origin);
    // Same origin only: following `return` onto another host would hand this
    // session's landing page — and anything the app puts in the URL — to it.
    //
    // And never onto a route that renders without a session. The invariant is
    // stronger than "not the login card": every one of them skips the `me`
    // query, so a live session looks anonymous there, and anything the
    // anonymous branch does not have a route for (/select-tenant/anything, or
    // /select-tenant itself with no refresh token) falls to the catch-all,
    // which sends the user back to /login, which sends them here again — a
    // full page load per lap. /login/google is refused for a sharper reason:
    // it starts a second social code exchange rather than rendering a page.
    if (url.origin !== origin || isAuthRoute(url.pathname)) {
      return bareUrl(pathname);
    }
    return url.href;
  } catch {
    return bareUrl(pathname);
  }
};
